/* eslint-disable consistent-return */
import React, { useMemo, useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import {
  Box,
  Paper,
  Popper,
  Button,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  ListSubheader,
  InputAdornment,
} from '@mui/material';

import {
  checkIsCitySudair,
  getPlusCodeResult,
  countriesBasedOnTypeAndEnvironment,
  filterPredictionFromPlaceId,
  filterPredictionFromPlusCode,
  notify,
  filteredPredictionFromTypes,
} from 'src/utils/helper';

import { useTranslate } from 'src/locales';
import { usePlacePredictions } from 'src/lib/location';
import { getLocationByCord, getLocationPrediction } from 'src/api/location/location';

import { Iconify } from '../iconify';
// Custom Popper
const CustomPopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
  />
);

// 🔥 RHF-Integrated Autocomplete
export default function RHFGooglePlacesAutocomplete({
  name,
  type,
  label = 'Enter Location',
  placeholder = 'Enter Location',
  helperText,
  disabled = false,
  onPlaceSelect,
  onSaveLocation,
  leftIconName = 'line-md:my-location',
  //   leftIconName = 'line-md:my-location-loop',
  leftIconColor = 'error.main',
  // savedLocations = [],
  onSavedLocationDelete,
  setGoogleMapsLinkData,
  setIsAddressPasted,
  isAddressPasted,
  isDisableForm,
}) {
  const {
    control,
    watch,
    formState: { disabled: isDisabled },
  } = useFormContext();
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslate();

  useEffect(() => {
    if (watch(name)) {
      setInputValue(watch(name)?.formatted_address);
    }
    if (!watch(name)) {
      setInputValue('');
    }
  }, [watch(name)]);

  const CustomPaper = (props) => (
    <Paper {...props}>
      {/* 👇 This Button will always appear at the top of the dropdown */}
      <Box sx={{ display: 'flex', width: '100%', px: 1, pt: 1, pb: 0, mb: 1 }}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          onMouseDown={(e) => e.preventDefault()}
          startIcon={<Iconify icon="material-symbols:map-search-rounded" />}
          sx={{}}
          onClick={(e) =>
            setGoogleMapsLinkData((prev) => ({
              ...prev,
              modal: true,
              locationType: type,
              locationName: name,
            }))
          }
        >
          {t('Click_to_Paste_Google_Maps_URL')}
        </Button>
      </Box>

      {props.children}

      <ListSubheader disableSticky>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <img
            src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
            alt="Powered by Google"
            width="120"
            height="14"
          />
        </Box>
      </ListSubheader>
    </Paper>
  );

  const { predictions, isLoading, isError, error, handlePaste } = usePlacePredictions(inputValue, {
    country: useMemo(() => countriesBasedOnTypeAndEnvironment(type), [type]),
  });

  const getOptionLabel = (option) => {
    if (!option) return '';

    if (predictions?.length) {
      return (
        option?.description ||
        option?.formatted_address ||
        (typeof option === 'string' ? option : '') ||
        ''
      );
    }

    // if (savedLocations?.length) {
    //   return (
    //     option?.name?.trim() ||
    //     option?.address?.trim() ||
    //     (typeof option === 'string' ? option : '') ||
    //     ''
    //   );
    // }

    // fallback if neither predictions nor saved locations match
    return (
      option?.formatted_address ||
      option?.description ||
      option?.name?.trim() ||
      (typeof option === 'string' ? option : '') ||
      ''
    );
  };

  const isOptionEqualToValue = (option, value) => {
    if (predictions?.length) {
      return option?.place_id === value?.place_id;
    }
    // if (savedLocations?.length) {
    //   return option?.favoriteLocationID === value?.favoriteLocationID;
    // }
  };

  const pasteAddress = (e) => {
    handlePaste(e);
    setIsAddressPasted(true);
  };

  const isCountry = (place) => place?.types?.some((placeType) => placeType?.includes('country'));

  // const filterPredictionFromPlaceId = async (newValue) => {
  //   if (newValue?.place_id) {
  //     const responseFromPlaceId = await getAddressFromPlaceId(newValue?.place_id);
  //     if (responseFromPlaceId?.length) {
  //       const response = await getPlusCodeResult({
  //         lat: responseFromPlaceId?.[0]?.geometry?.location.lat() || '',
  //         lng: responseFromPlaceId?.[0]?.geometry?.location.lng() || '',
  //       });
  //       const filteredPrediction =
  //         checkIsCitySudair(response) ||
  //         filteredPredictionFromTypes(response) ||
  //         response?.predictions?.[0];

  //       const updatedPrediction = {
  //         ...filteredPrediction,
  //         geometry: {
  //           ...filteredPrediction?.geometry,
  //           location: {
  //             lat: Number(responseFromPlaceId?.[0]?.geometry?.location.lat()),
  //             lng: Number(responseFromPlaceId?.[0]?.geometry?.location.lng()),
  //           },
  //         },
  //       };
  //       return updatedPrediction;
  //     }
  //   }
  // };

  // const filterPredictionFromPlusCode = async (newValue) => {
  //   const response = await getPlusCodeResult({
  //     lat: newValue?.geometry?.location.lat() || '',
  //     lng: newValue?.geometry?.location.lng() || '',
  //   });
  //   const filteredPrediction =
  //     checkIsCitySudair(response) ||
  //     filteredPredictionFromTypes(response) ||
  //     response?.predictions?.[0];
  //   const updatedPrediction = {
  //     ...filteredPrediction,
  //     geometry: {
  //       ...filteredPrediction?.geometry,
  //       location: {
  //         lat: Number(newValue?.geometry?.location.lat()),
  //         lng: Number(newValue?.geometry?.location.lng()),
  //       },
  //     },
  //   };
  //   return updatedPrediction;
  // };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => (
        <Autocomplete
          {...field}
          fullWidth
          freeSolo
          disabled={disabled || isDisabled}
          options={(predictions?.length > 0 && predictions) || []}
          PopperComponent={CustomPopper}
          PaperComponent={CustomPaper}
          getOptionLabel={getOptionLabel}
          filterOptions={(options) => options}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={isLoading}
          noOptionsText="No locations found"
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason !== 'reset') {
              setInputValue(newInputValue);
            }
          }}
          onChange={async (event, newValue) => {
            if (newValue) {
              let res = null;
              if (newValue?.favoriteLocationID) {
                res = await getLocationByCord(`${newValue?.lat},${newValue.long}`);
              } else {
                res = await getLocationPrediction(newValue?.description);
              }
              if (res?.predictions?.length) {
                field.onChange(res?.predictions?.[0]);
                if (newValue?.favoriteLocationID) {
                  const updatedPrediction = {
                    ...res?.predictions?.[0],
                    formatted_address: newValue?.description || newValue?.address,
                    city: newValue?.cityName,
                    ...newValue,
                    geometry: {
                      location: {
                        lat: Number(newValue?.lat),
                        lng: Number(newValue?.long),
                      },
                    },
                  };

                  const filteredPrediction = checkIsCitySudair(res) || updatedPrediction;
                  const selectedPrediction = {
                    ...filteredPrediction,
                    geometry: {
                      ...filteredPrediction?.geometry,
                      location: {
                        lat: Number(newValue?.lat),
                        lng: Number(newValue?.long),
                      },
                    },
                  };
                  //
                  // onPlaceSelect?.({
                  //   geometry: { location: { lat: newValue?.lat, lng: newValue?.long } },
                  // });
                  onPlaceSelect?.(selectedPrediction, type, name);
                } else if (isAddressPasted) {
                  const plusCodeRegex =
                    /([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3})/i;
                  if (plusCodeRegex.test(newValue?.description)) {
                    if (newValue?.geometry?.location) {
                      const filteredPrediction = await filterPredictionFromPlusCode(newValue);
                      if (Object.keys(filteredPrediction)?.length)
                        onPlaceSelect?.(filteredPrediction, type, name);
                    }
                    if (!newValue?.geometry?.location) {
                      const filteredPrediction = await filterPredictionFromPlaceId(newValue);
                      if (Object.keys(filteredPrediction)?.length)
                        onPlaceSelect?.(filteredPrediction, type, name);
                    }
                  } else {
                    if (newValue?.geometry?.location) {
                      const response = await getPlusCodeResult({
                        lat: newValue?.geometry?.location.lat() || '',
                        lng: newValue?.geometry?.location.lng() || '',
                      });
                      onPlaceSelect?.(response?.predictions?.[0], type, name);
                    }
                    if (!newValue?.geometry?.location) {
                      const filteredPrediction = await filterPredictionFromPlaceId(newValue);
                      if (Object.keys(filteredPrediction)?.length)
                        onPlaceSelect?.(filteredPrediction, type, name);
                    }
                  }
                } else {
                  const plusCodeRegex =
                    /([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3})/i;
                  if (plusCodeRegex.test(newValue?.description)) {
                    if (newValue?.geometry?.location) {
                      const filteredPrediction = await filterPredictionFromPlusCode(newValue);
                      if (Object.keys(filteredPrediction)?.length)
                        onPlaceSelect?.(filteredPrediction, type, name);
                    }
                    if (!newValue?.geometry?.location) {
                      const filteredPrediction = await filterPredictionFromPlaceId(newValue);
                      if (Object.keys(filteredPrediction)?.length)
                        onPlaceSelect?.(filteredPrediction, type, name);
                    }
                  } else {
                    const selectedPlace = res;
                    if (!selectedPlace) return;

                    //  Block country selection first
                    if (isCountry(selectedPlace?.predictions?.[0])) {
                      notify('error', t('Please_select_a_valid_location_instead_of_a_country'));
                      return;
                    }

                    const selectedPrediction =
                      filteredPredictionFromTypes(selectedPlace) || selectedPlace;

                    // 1️ If geometry exists, use it directly
                    if (newValue?.geometry?.location) {
                      onPlaceSelect?.(selectedPrediction, type, name);
                      return;
                    }

                    // 2️ If types are NOT valid ,fetch placeId details
                    if (!filteredPredictionFromTypes(selectedPlace)) {
                      const filteredPrediction = await filterPredictionFromPlaceId(newValue);

                      if (filteredPrediction && Object.keys(filteredPrediction).length) {
                        onPlaceSelect?.(filteredPrediction, type, name);
                        return;
                      }
                    }

                    // 3️ Fallback
                    onPlaceSelect?.(selectedPrediction, type, name);
                  }
                }
              }
            } else {
              field.onChange('');
              onPlaceSelect?.('', type, name);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              fullWidth
              error={!!fieldError}
              onPaste={(e) => pasteAddress(e)}
              onFocus={() => {
                setIsAddressPasted(false);
              }}
              helperText={fieldError?.message || error?.message || helperText}
              slotProps={{
                formHelperText: {
                  sx: {
                    ml: 0,
                    mt: 0,
                  },
                },
              }}
              InputProps={{
                ...params.InputProps,
                style: {
                  paddingRight: params.InputProps.endAdornment ? 10 : 5,
                  paddingLeft: params.InputProps.startAdornment ? 5 : 10,
                },
                endAdornment: (
                  <>
                    {isLoading && (
                      <Iconify icon="line-md:loading-twotone-loop" sx={{ color: 'inherit' }} />
                    )}
                    {/* {field.value && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => onSaveLocation?.(field.value)}
                          disabled={isDisableForm}
                        >
                          <BookmarkBorderOutlinedIcon color="primary" />
                        </IconButton>
                      </InputAdornment>
                    )} */}
                    {params.InputProps.endAdornment}
                  </>
                ),
                startAdornment: (
                  <InputAdornment>
                    <Iconify icon={leftIconName} sx={{ color: leftIconColor }} />
                  </InputAdornment>
                ),
              }}
              autoComplete="new-password"
            />
          )}
          renderOption={
            predictions?.length > 0
              ? undefined
              : (renderProps, option, { index }) => (
                  <Box key={option.favoriteLocationID}>
                    {index === 0 && (
                      <Box>
                        <Box
                          sx={{
                            px: 1,
                            pt: 1,
                            pb: 0.5,
                            width: '100%',
                            flexDirection: 'row',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          <BookmarkBorderOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
                          <Typography variant="subtitle2" color="primary" fontWeight={700}>
                            {t('Saved_Locations')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    <Box
                      component="li"
                      {...renderProps}
                      key={option.id}
                      display="flex"
                      alignItems="center"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Box flexGrow={1} minWidth={0} pr={1}>
                          <Typography variant="subtitle2" noWrap>
                            {option?.name?.trim()}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }} noWrap>
                            {option.cityName}, {option.address}
                          </Typography>
                        </Box>
                        <IconButton
                          color="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSavedLocationDelete?.(option);
                          }}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start', // 👈 aligns to the top line of the text
                            mt: 0.5, // Optional: fine-tune vertical alignment
                          }}
                          aria-label="delete saved location"
                        >
                          <Iconify
                            icon="material-symbols:delete-outline-rounded"
                            sx={{ color: 'error.main' }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                )
          }
        />
      )}
    />
  );
}
